import { createFileRoute, Link } from '@tanstack/react-router'
import { CalendarDaysIcon, UserGroupIcon, BriefcaseIcon } from '@heroicons/react/24/outline'

export const Route = createFileRoute('/')({
  component: HomeComponent,
})

function HomeComponent() {
  return (
    <div className="w-full bg-base-100">
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .float-animation {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-4rem)] py-12 lg:py-0">
        <div className="flex flex-col-reverse lg:flex-row items-center justify-between gap-8 lg:gap-12 min-h-[calc(100vh-4rem)]">
          
          {/* Left Content */}
          <div className="flex-1 max-w-2xl pb-12 lg:pb-0 text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              Streamline your team scheduling and appointment management
            </h1>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Link 
                to="/register"
                className="btn bg-accent text-white hover:bg-accent/90 btn-lg px-8"
              >
                Get Started
              </Link>
              <Link 
                to="/login"
                className="btn btn-outline btn-lg px-8"
              >
                Sign In
              </Link>
            </div>
          </div>

          {/* Right Image */}
          <div className="flex-1 w-full max-w-2xl">
            <img 
              src="/images/hero-calendar.png" 
              alt="Calendar scheduling illustration"
              className="w-full h-auto object-contain max-h-[500px] lg:max-h-[600px] float-animation rounded-2xl shadow-2xl"
              style={{ display: 'block' }}
            />
          </div>

        </div>
      </div>

      {/* Features Section */}
      <div className="w-full py-16 lg:py-24" style={{ backgroundColor: '#515050' }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
              Why Choose <span className="text-accent">zkejul</span>?
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Everything you need to manage your team scheduling and appointments in one powerful platform.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            
            {/* Feature 1 */}
            <div className="card bg-base-100 border border-white/20 shadow-lg hover:shadow-xl transition-shadow">
              <div className="card-body items-center text-center">
                <CalendarDaysIcon className="w-16 h-16 mb-4 text-accent" />
                <h3 className="card-title text-xl mb-2">Smart Calendar</h3>
                <p className="text-secondary">
                  Intuitive calendar interface to view and manage appointments with real-time updates and scheduling conflicts prevention.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="card bg-base-100 border border-white/20 shadow-lg hover:shadow-xl transition-shadow">
              <div className="card-body items-center text-center">
                <UserGroupIcon className="w-16 h-16 mb-4 text-accent" />
                <h3 className="card-title text-xl mb-2">Team Management</h3>
                <p className="text-secondary">
                  Coordinate multiple team members, track their schedules, and manage appointments across your entire organization.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="card bg-base-100 border border-white/20 shadow-lg hover:shadow-xl transition-shadow">
              <div className="card-body items-center text-center">
                <BriefcaseIcon className="w-16 h-16 mb-4 text-accent" />
                <h3 className="card-title text-xl mb-2">Customer Profiles</h3>
                <p className="text-secondary">
                  Comprehensive customer management with detailed profiles, appointment history, and integrated insurance tracking.
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="w-full bg-base-200 py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Ready to streamline your scheduling?
          </h2>
          <p className="text-lg text-secondary mb-8 max-w-2xl mx-auto">
            Join teams already using zkejul to manage their appointments efficiently.
          </p>
          <Link 
            to="/register"
            className="btn bg-accent text-white hover:bg-accent/90 btn-lg px-10"
          >
            Get Started for Free
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full bg-neutral py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/80 text-center md:text-left">
              <p className="font-bold text-accent text-lg mb-1">zkejul</p>
              <p className="text-sm">Team scheduling made simple</p>
            </div>
            <div className="text-white/60 text-sm text-center md:text-right">
              © 2026 jetrotullroa. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
